import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import BookmarkList from '../components/BookmarkList';
import LayoutSelector from '../components/layouts/LayoutSelector';
import type { LayoutConfig } from '../types/layout';
import { useUserProfile } from '../services/dataHooks';

export const Route = createFileRoute('/p/$id')({
  component: UserProfileRoute,
});

// Types and fetch logic moved to types/api and services/dataHooks

interface UserProfileContentProps {
  userId: string;
  page: number;
}

export function UserProfileContent({ userId, page }: UserProfileContentProps) {
  useLingui();
  const { profile: data, isLoading: isPending, error } = useUserProfile(userId, page);

  const layoutConfig: LayoutConfig = {
    type: 'hybrid',
    showAuthButtons: true,
  };

  const hasPagination = page > 1 || data?.next_page;
  const showNewerArticles = page > 1;
  const showOlderArticles = data?.next_page;

  const newerHref = showNewerArticles
    ? page - 1 === 1
      ? { to: '/p/$id', params: { id: `${userId}` } }
      : { to: '/p/$id/$page', params: { id: `${userId}`, page: `${page - 1}` } }
    : null;
  const olderHref = showOlderArticles
    ? { to: '/p/$id/$page', params: { id: `${userId}`, page: `${page + 1}` } }
    : null;

  return (
    <LayoutSelector
      config={layoutConfig}
      title={data?.user.display_name || data?.user.username || t`Not Found`}
    >
      {error ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center text-gray-600">
          <p>
            <Trans>Sorry, there was an error. Please try again.</Trans>
          </p>
        </div>
      ) : (
        <>
          <div className="z-high sticky top-0 border-b border-gray-300 bg-white px-4 py-5">
            <h1 className="text-lg font-semibold">
              {data?.user.display_name || data?.user.username || t`Loading...`}
            </h1>
          </div>

          <BookmarkList
            bookmarks={data?.bookmarks || []}
            isLoading={isPending}
            mode="liked"
            profileMode={true}
            showThumbnails={true}
          />

          {hasPagination && (
            <div className="text-copy-secondary flex justify-between border-t border-gray-300 py-6 text-sm">
              {showNewerArticles ? (
                newerHref && (
                  <Link {...newerHref} className="px-4 py-2">
                    ← <Trans>Newer Articles</Trans>
                  </Link>
                )
              ) : (
                <div />
              )}

              {showOlderArticles && olderHref && (
                <Link {...olderHref} className="px-4 py-2">
                  <Trans>Older Articles</Trans> →
                </Link>
              )}
            </div>
          )}
        </>
      )}
    </LayoutSelector>
  );
}

function UserProfileRoute() {
  return <Outlet />;
}
